import { DatePipe } from '@angular/common';
import { Component, computed, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery } from '@examdojo/category';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { Icon, IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { provideJsonEditor } from '@examdojo/core/json-editor';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionItemsPreviewComponent } from '@examdojo/question';
import { UserQuery, UserService } from '@examdojo/user';
import { ModalController } from '@ionic/angular/standalone';
import questionItemsSchema from '@settings/question_items_schema.json';
import { AngularSplitModule } from 'angular-split';
import { DiffEditorComponent } from 'ngx-monaco-editor-v2';
import { EMPTY, map, switchMap } from 'rxjs';
import { QuestionHistoryConfig, QuestionHistoryEntry } from './generic-question-history.model';
import { GenericQuestionHistoryService } from './generic-question-history.service';

@Component({
  selector: 'exam-generic-question-history-dialog',
  templateUrl: './generic-question-history-dialog.component.html',
  imports: [
    AngularSplitModule,
    IconButtonComponent,
    IconComponent,
    QuestionItemsPreviewComponent,
    DatePipe,
    ButtonComponent,
    MatTooltipModule,
    MatButtonToggleModule,
    DiffEditorComponent,
  ],
  providers: [provideJsonEditor(questionItemsSchema)],
  host: { class: 'h-full flex flex-col' },
})
export class GenericQuestionHistoryDialogComponent {
  constructor(
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly modalCtrl: ModalController,
    private readonly userService: UserService,
    private readonly userQuery: UserQuery,
    private readonly genericHistoryService: GenericQuestionHistoryService,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    this.fetchUsersForHistoryEntries().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly selectedVersion = signal<EnhancedHistoryEntry | null>(null);
  readonly selectedVersions = signal<EnhancedHistoryEntry[]>([]);
  readonly isDiffMode = signal<boolean>(false);

  readonly showEdits = signal<boolean>(true);
  readonly showStatusChanges = signal<boolean>(true);
  readonly showReviewFeedback = signal<boolean>(true);

  readonly config = input<QuestionHistoryConfig>();

  readonly history$ = toObservable(this.config).pipe(
    switchMap((config) => {
      if (!config) {
        return EMPTY;
      }

      return this.genericHistoryService.getQuestionHistory(config.questionId, config.questionType);
    }),
  );

  readonly markCalculationMethod$ = this.syllabusQuery.markCalculationMethod$;

  readonly history = toSignal(this.history$);

  readonly filteredHistoryEntries = computed<QuestionHistoryEntry[] | null>(() => {
    const entries = this.history();
    if (!entries) {
      return null;
    }

    return entries.filter((entry) => {
      switch (entry.type) {
        case 'edit':
          return this.showEdits();
        case 'status_change':
          return this.showStatusChanges();
        case 'review_feedback':
          return this.showReviewFeedback();
        default:
          return true;
      }
    });
  });

  readonly enhancedHistory = computed(() => {
    const entries = this.filteredHistoryEntries() ?? [];
    return entries.map((entry) => ({
      ...entry,
      versionDisplayName: this.getVersionDisplayName(entry),
      formattedFeedback:
        entry.type === 'review_feedback' && entry.feedback ? JSON.stringify(entry.feedback, null, 2) : '',
    }));
  });

  readonly state = connectState({
    users: this.userQuery.entities$.pipe(
      map((users) =>
        Object.fromEntries(
          users.map((user) => {
            return [
              user.id,
              {
                id: user.id,
                fullName: user.fullName,
                email: user.email,
              },
            ];
          }),
        ),
      ),
    ),
    markCalculationMethod: this.markCalculationMethod$,
  });

  readonly diffOptions = {
    automaticLayout: true,
    theme: 'vs',
    language: 'json',
    scrollBeyondLastLine: false,
    minimap: { enabled: true },
    lineHeight: 20,
    fontSize: 14,
    wordWrap: 'on',
    wrappingIndent: 'indent',
  };

  readonly iconMap: Record<QuestionHistoryEntry['type'], Icon> = {
    edit: 'edit',
    status_change: 'swap-arrows',
    review_feedback: 'comment',
  };

  readonly diffData = computed(() => {
    const selected = this.selectedVersions();
    if (selected.length !== 2) {
      return null;
    }

    const sorted = [...selected].sort(
      (a, b) => new Date(a.timestamp ?? Date.now()).getTime() - new Date(b.timestamp ?? Date.now()).getTime(),
    );
    return {
      older: sorted[0],
      newer: sorted[1],
    };
  });

  readonly selectedVersionsSet = computed(() => {
    const selected = this.selectedVersions();
    return new Set(selected.map((v) => v.id));
  });

  readonly diffOriginalModel = computed(() => ({
    language: 'json',
    code: JSON.stringify(this.diffData()?.older.question_items, null, 2),
  }));

  readonly diffModifiedModel = computed(() => ({
    language: 'json',
    code: JSON.stringify(this.diffData()?.newer.question_items, null, 2),
  }));

  selectVersion(version: EnhancedHistoryEntry) {
    if (this.isDiffMode()) {
      this.toggleVersionForDiff(version);
    } else {
      this.selectedVersion.set(version);
    }
  }

  toggleVersionForDiff(version: EnhancedHistoryEntry) {
    const currentSelected = this.selectedVersions();
    const index = currentSelected.findIndex((v) => v.id === version.id);

    if (index >= 0) {
      const newSelected = [...currentSelected];
      newSelected.splice(index, 1);
      this.selectedVersions.set(newSelected);
    } else if (currentSelected.length < 2) {
      this.selectedVersions.set([...currentSelected, version]);
    }
  }

  toggleDiffMode() {
    const newDiffMode = !this.isDiffMode();
    this.isDiffMode.set(newDiffMode);

    if (!newDiffMode) {
      const selected = this.selectedVersions();
      if (selected.length > 0) {
        const mostRecent = [...selected].sort(
          (a, b) => new Date(b.timestamp ?? Date.now()).getTime() - new Date(a.timestamp ?? Date.now()).getTime(),
        )[0];
        this.selectedVersion.set(mostRecent);
      }
      this.selectedVersions.set([]);
    } else {
      const currentSelection = this.selectedVersion();
      if (currentSelection) {
        this.selectedVersions.set([currentSelection]);
      }
      this.selectedVersion.set(null);
    }
  }

  toggleEditFilter() {
    this.showEdits.set(!this.showEdits());
  }

  toggleStatusChangeFilter() {
    this.showStatusChanges.set(!this.showStatusChanges());
  }

  toggleReviewFeedbackFilter() {
    this.showReviewFeedback.set(!this.showReviewFeedback());
  }

  closeDialog() {
    this.modalCtrl.dismiss();
  }

  private getVersionDisplayName(entry: QuestionHistoryEntry): string {
    switch (entry.type) {
      case 'edit':
        return 'Edit';
      case 'status_change':
        return `Status: ${entry.status || 'Unknown'}`;
      case 'review_feedback':
        return `Review: ${entry.action_type || 'Feedback'}`;
      default:
        return 'Unknown';
    }
  }

  private fetchUsersForHistoryEntries() {
    return this.history$.pipe(
      switchMap((history) => {
        const userIds = [...new Set(history.map((entry) => entry.user_id).filter(isNotNullish))];

        return this.userService.fetchUsersByIds(userIds);
      }),
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
    );
  }
}

export type EnhancedHistoryEntry = QuestionHistoryEntry & {
  versionDisplayName: string;
  formattedFeedback: string;
};
